import { inject, Injectable, Type } from '@angular/core';
import { Observable, take } from 'rxjs';
import { addHours, isBefore } from 'date-fns';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { AuthenticationService } from '@lb/auth/services/authentication.service';
import { Role } from '@lb/enums/role.enum';
import { IAlert, MDAlertService, MDDialogService } from '@melodic-ui/components';
import { LearnerRescheduleLessonDialogV2Component } from '@lb/components/reschedule-lesson-dialog/learner-reschedule-lesson-dialog-v2/learner-reschedule-lesson-dialog-v2.component';
import { InstructorRescheduleLessonDialogV2Component } from '@lb/components/reschedule-lesson-dialog/instructor-reschedule-lesson-dialog-v2/instructor-reschedule-lesson-dialog-v2.component';
import { RescheduleLateDialogComponent } from '../components/reschedule-lesson-dialog/reschedule-late-dialog/reschedule-late-dialog.component';

/**
 * Service for opening reschedule lesson dialogs.
 * Automatically displays the appropriate dialog based on the current user's role.
 */
@Injectable({
	providedIn: 'root'
})
export class RescheduleLessonDialogService {
	private readonly _dialogService = inject(MDDialogService);
	private readonly _authService = inject(AuthenticationService);
	private readonly _alertService = inject(MDAlertService);

	/**
	 * Opens the appropriate reschedule dialog based on the current user's role.
	 * @param lesson The lesson to reschedule
	 * @param forceUserType Optional parameter to force a specific user type (useful for learner app)
	 * @returns Observable that emits when the dialog is closed
	 */
	open(lesson: ILessonListItem, forceUserType?: 'learner' | 'instructor'): Observable<any> {
		const dialogComponent = this.getDialogComponent(lesson, forceUserType);
		const dialogRef = this._dialogService.open(dialogComponent, { lesson });

		// Handle success alerts for all dialogs
		dialogRef.afterClosed.pipe(take(1)).subscribe((result) => {
			if (result) {
				this._alertService.addAlert({
					type: 'success',
					title: 'The lesson has been successfully rescheduled.',
					duration: 3000
				} as IAlert);
			}
		});

		return dialogRef.afterClosed;
	}

	/**
	 * Determines which dialog component to use based on user type, lesson timing, and forced type.
	 * @param lesson The lesson to reschedule
	 * @param forceUserType Optional parameter to force a specific user type
	 * @returns The appropriate dialog component
	 */
	private getDialogComponent(lesson: ILessonListItem, forceUserType?: 'learner' | 'instructor'): Type<any> {
		// Check if the lesson is within 24 hours and if user is a learner (or forced to learner type)
		const currentUser = this._authService.getUser();
		const currentPlus24Hours = addHours(new Date(), 24);
		const isInNext24Hours = isBefore(lesson.lessonDate, currentPlus24Hours);
		const isLearnerUser = forceUserType === 'learner' || this.isLearnerBasedOnRole(forceUserType);

		// Show late reschedule dialog for learners with lessons in next 24 hours (excluding makeup lessons)
		if (isInNext24Hours && !lesson.makeUp && isLearnerUser && currentUser?.roleID !== Role.ADMIN) {
			return RescheduleLateDialogComponent;
		}

		// For all other cases, use the standard dialog selection logic
		if (forceUserType === 'learner') {
			return LearnerRescheduleLessonDialogV2Component;
		}

		if (forceUserType === 'instructor') {
			return InstructorRescheduleLessonDialogV2Component;
		}

		// Auto-detect based on user role
		const isInstructor = currentUser?.roleID === Role.INSTRUCTOR || currentUser?.roleID === Role.ADMIN;

		return isInstructor ? InstructorRescheduleLessonDialogV2Component : LearnerRescheduleLessonDialogV2Component;
	}

	/**
	 * Determines if the current user is a learner based on role or forced type.
	 * @param forceUserType Optional parameter to force a specific user type
	 * @returns True if user should be treated as a learner
	 */
	private isLearnerBasedOnRole(forceUserType?: 'learner' | 'instructor'): boolean {
		if (forceUserType === 'learner') {
			return true;
		}

		if (forceUserType === 'instructor') {
			return false;
		}

		// Auto-detect based on user role
		const currentUser = this._authService.getUser();
		const isInstructor = currentUser?.roleID === Role.INSTRUCTOR || currentUser?.roleID === Role.ADMIN;

		return !isInstructor;
	}
}
